import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux';
import { mapFrequencyNameToTranslation } from './CashbackUtils';

import CashbackUserLevel from './CashbackUserLevel';
import CashbackButton from './CashbackButton';
import CashBackTimer from './CashBackTimer';

import moment from 'moment';
import { showCashier } from '../../../../payments/DepositActions';
import { AppState, useAppSelector } from '../../../../RootReducer';
import { fetchUserLevelView, getUpComingCashbackForPublic, getUpComingCashbackForUser } from '../../../../user/UserActions';
import { RequestCashbackStatus } from '../../../../http/Types';
import { t } from '../../../../i18n';
import { NextUpcomingCashback, NotEligibleReason } from '../../../../http/protocol';
import { getLoggedIn, getUserLevelView } from '../../../../selectors/user-selectors';



export default function CashbackComponent(props: any): React.ReactElement {

    const userInfoLoaded = useAppSelector((app: AppState) => app.user.userInfoLoaded);
    const loggedIn = useAppSelector((app: AppState) => getLoggedIn(app));
    const userLevel = useAppSelector((app: AppState) => getUserLevelView(app));

    const cashbacks = useAppSelector((app: AppState) => app.user.cashbacks) ?? [];
    const cashbackRequestStatus = useAppSelector((app: AppState) => app.user.requestCachbackStatus);
    const showDepositModal = () => dispatch(showCashier(true));
    const dispatch = useDispatch();

    const nextLevelPercentage: number | undefined = userLevel && userLevel.nextLevelProgressionPercentage;

    const useFetchWithLimit = (callback: () => void, maxCalls: number, timeLimitMs: number) => {
        const callCount = useRef(0);
        const lastResetTime = useRef(Date.now());

        const limitedCallback = useCallback(() => {
            const now = Date.now();

            if (now - lastResetTime.current > timeLimitMs) {
                callCount.current = 0;
                lastResetTime.current = now;
            }

            if (callCount.current < maxCalls) {
                callCount.current++;
                callback();
            }
        }, [callback, maxCalls, timeLimitMs]);

        return limitedCallback;
    };

    const refetchCashbackWhenFrequencyTurnsZeros = useCallback(() => {
        if (loggedIn) {
            dispatch(getUpComingCashbackForUser() as any);
        } else {
            dispatch(getUpComingCashbackForPublic() as any);
        }
    }, [loggedIn, dispatch]);

    // Safe refetch with limit, to avoid too many requests if frequency is set to 0 for some reason. ( safety net )
    const safeRefetch = useFetchWithLimit(refetchCashbackWhenFrequencyTurnsZeros, 5, 60000);

    // fetch cashback when user logs in and logs out ( different cashback for public and user )
    useEffect(() => {
        if (userInfoLoaded) {
            safeRefetch();
        }
    }, [loggedIn, userInfoLoaded, safeRefetch]);

    // fetch userlevel view if not already fetched ( used in progression bar if not eligible for cashback = no award rate)
    useEffect(() => {
        if (loggedIn && userLevel && Object.keys(userLevel).length === 0) {
            dispatch(fetchUserLevelView() as any);
        }
    }, [loggedIn])


    const singleCashback = (cashbacks && cashbacks[0]) ?? {};
    const isCashbackEmpty = Object.keys(singleCashback).length === 0;
    const isRequestCashbackSuccess = cashbackRequestStatus == RequestCashbackStatus.SUCCESS;


    if (isCashbackEmpty) {
        return <></>
    }

    const mapCashbackFrequencyToTimer = () => {
        if (singleCashback.payoutOngoing) {
            return <h3 className="being-paid-out">{t("cashback.beingPayedOut")}</h3>;
        }
        if (singleCashback) {
            return <CashBackTimer key={singleCashback.configName} scheduled={singleCashback.scheduled} refetchCashback={safeRefetch} />;
        }
    }

    return (
        <div className="cashback-component">
            <div className="cashback-header-container">{<h4>{mapFrequencyNameToTranslation(singleCashback.frequency)}</h4>}</div>
            <div className="cashback-timer-container">
                {isRequestCashbackSuccess && mapCashbackFrequencyToTimer()}

            </div>
            {loggedIn && (
                <div className="cashback-action-container">
                    <CashbackActionBasedOnStatus cashback={singleCashback} nextLevelPercentage={nextLevelPercentage} />
                    <button data-testid="depositBtn" className="btn btn-cta top-deposit cashback-deposit-btn" onClick={showDepositModal}>
                        {t("cashback.depositButton")}
                    </button>
                </div>
            )}
        </div>
    )
}

const CashbackActionBasedOnStatus = ({ cashback, nextLevelPercentage }: { cashback: NextUpcomingCashback, nextLevelPercentage?: number }) => {
    if (cashback.notEligibleReason === NotEligibleReason.NO_AWARD_RATE) {
        return (
            <CashbackUserLevel nextLevelPercentage={nextLevelPercentage} />
        )
    } else {
        return <CashbackButton amount={cashback.amount ?? 0} currencyCode={cashback.currency ?? "EUR"} />
    }
}





